import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Loader, Menu } from '@mantine/core';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationService,
  useSessionStatusService,
  useSessionProgressStatusService,
  type RegistrationSessionModelView,
  type SessionStatus,
  type SessionProgressStatus,
} from '@actschurch/ccms-sdk';

interface SessionStateMenuProps {
  session: RegistrationSessionModelView;
  kind: 'status' | 'progress';
  onUpdated?: (session: RegistrationSessionModelView) => void;
}

export default function SessionStateMenu({ session, kind, onUpdated }: SessionStateMenuProps) {
  const registrationService = useRegistrationService();
  const statusService = useSessionStatusService();
  const progressStatusService = useSessionProgressStatusService();

  const [menuOpened, setMenuOpened] = useState(false);
  const [statusOptions, setStatusOptions] = useState<SessionStatus[]>([]);
  const [progressOptions, setProgressOptions] = useState<SessionProgressStatus[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [localSession, setLocalSession] = useState(session);

  useEffect(() => {
    setLocalSession(session);
  }, [session]);

  const loadOptions = useCallback(async () => {
    if (loadingOptions) return;
    setLoadingOptions(true);

    try {
      if (kind === 'status') {
        const statuses = await statusService.getAll();
        setStatusOptions((statuses ?? []).filter((item) => item.isActive));
      } else {
        const progress = await progressStatusService.getAll();
        setProgressOptions((progress ?? []).filter((item) => item.isActive));
      }
    } catch (error) {
      console.error(`Error loading ${kind} options:`, error);
      toast.error('Error', `Could not load ${kind === 'status' ? 'status' : 'progress'} options.`);
    } finally {
      setLoadingOptions(false);
    }
  }, [kind, loadingOptions, progressStatusService, statusService]);

  useEffect(() => {
    if (!menuOpened) return;
    if (kind === 'status') {
      if (statusOptions.length > 0 || loadingOptions) return;
    } else if (progressOptions.length > 0 || loadingOptions) {
      return;
    }

    void loadOptions();
  }, [kind, loadOptions, loadingOptions, menuOpened, progressOptions.length, statusOptions.length]);

  const label = useMemo(() => {
    if (kind === 'status') {
      return localSession.effectiveStatusName ?? localSession.statusName ?? '—';
    }
    return localSession.progressStatusName ?? '—';
  }, [kind, localSession]);

  const color = useMemo(() => {
  if (kind === 'status') {
    return localSession.effectiveStatusColor ?? localSession.statusColor ?? undefined;
  }
  return localSession.progressStatusColor ?? undefined;
}, [kind, localSession]);

  const handleSelect = async (newValue: number) => {
    if (!localSession.registrationSessionID || updating) return;

    setUpdating(true);
    try {
      if (kind === 'status') {
        await registrationService.changeSessionStatus(localSession.registrationSessionID, newValue);
      } else {
        await registrationService.changeSessionProgress(localSession.registrationSessionID, newValue);
      }

      const nextSession = { ...localSession } as RegistrationSessionModelView;
      if (kind === 'status') {
        const selected = statusOptions.find((option) => option.statusID === newValue);
        nextSession.statusID = newValue;
        nextSession.statusName = selected?.name ?? nextSession.statusName;
        nextSession.statusColor = selected?.color ?? nextSession.statusColor;
        nextSession.effectiveStatusName = selected?.name ?? nextSession.effectiveStatusName ?? nextSession.statusName;
        nextSession.effectiveStatusColor = selected?.color ?? nextSession.effectiveStatusColor ?? nextSession.statusColor;
      } else {
        const selected = progressOptions.find((option) => option.statusID === newValue);
        nextSession.progressStatusID = newValue;
        nextSession.progressStatusName = selected?.name ?? nextSession.progressStatusName;
        nextSession.progressStatusColor = selected?.color ?? nextSession.progressStatusColor;
      }

      setLocalSession(nextSession);
      onUpdated?.(nextSession);

      toast.success(
        kind === 'status' ? 'Status Updated' : 'Progress Updated',
        kind === 'status' ? 'Session status has been updated.' : 'Session progress has been updated.'
      );
    } catch (error) {
      console.error(`Error updating session ${kind}:`, error);
      toast.error('Error', `Could not update session ${kind === 'status' ? 'status' : 'progress'}.`);
    } finally {
      setUpdating(false);
      setMenuOpened(false);
    }
  };

  const options = kind === 'status' ? statusOptions : progressOptions;
  const menuLabel = kind === 'status' ? 'Change status' : 'Change progress';

  return (
    <Menu opened={menuOpened} onChange={setMenuOpened} width={220} position="bottom-start">
      <Menu.Target>
        <Badge
          component="button"
          type="button"
          variant="light"
          size="sm"
          onClick={(event) => event.stopPropagation()}
          style={{
            cursor: 'pointer',
            backgroundColor: color ? `${color}22` : undefined,
            color,
            border: color ? `1px solid ${color}55` : undefined,
            opacity: updating ? 0.7 : 1,
          }}
        >
          {updating ? <Loader size={12} /> : label}
        </Badge>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{menuLabel}</Menu.Label>
        {loadingOptions ? (
          <Menu.Item disabled>
            <Loader size={12} />
          </Menu.Item>
        ) : (
          options.map((option) => {
            const optionId = option.statusID;
            const optionLabel = option.name ?? `Option ${optionId}`;
            const optionColor = 'color' in option ? option.color : undefined;

            return (
              <Menu.Item
                key={optionId}
                onClick={() => void handleSelect(optionId)}
                disabled={optionId === (kind === 'status' ? localSession.statusID : localSession.progressStatusID)}
                leftSection={
                  optionColor ? (
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: optionColor,
                        flexShrink: 0,
                      }}
                    />
                  ) : null
                }
              >
                {optionLabel}
              </Menu.Item>
            );
          })
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
